'use strict';

BaseGridGridFactory.$inject = [
	'$q',
	'$mdUtil',
	'mx.shell.NotificationService',
	'mx.internationalization',
	'mx.SolutionBuilderAgent.MxGridHelperService',
	'mx.SolutionBuilderAgent.GridEnums',
];
export default function BaseGridGridFactory($q, $mdUtil, notificationService, i18n, gridHelperService, GridEnums) {
	class BaseGrid {
		constructor() {
			this.debouncedLoadItems = $mdUtil.debounce(this.loadItems.bind(this), 100);
			this.initProperties();
		}

		async init(personalizationKey) {
			this.personalizationKey = personalizationKey;

			const config = {
				preprocessors: {
					paging: {
						enabled: this.enablePaging,
					},
					sorting: {
						enabled: this.enableSorting,
					},
					search: {
						enabled: this.enableSearch,
					},
					columnSearch: {
						enabled: this.enableColumnSearch,
					},
					advanceSearch: {
						enabled: this.enableAdvanceSearch,
					},
				},
			};

			this.executor = await this.initDataQueryExecutor(config);
			this.createItems = await this.initCreateButtonItems();

			this.updateRequest();

			this.executor.addSelectionHandler(this.selectionHandler.bind(this));

			this.initialized = true;
			this.executor.loading = true;
			this.debouncedLoadItems();
		}

		initProperties() {
			var editMode = GridEnums.EditMode.None,
				disableEdit = false,
				headerHidden = false,
				showActionsPanel = false,
				showSelectAll = false,
				inlineMode = false,
				enableDragAndDrop = false,
				enableSorting = true,
				enablePaging = true,
				enableSearch = true,
				enableColumnSearch = true,
				enableAdvanceSearch = false,
				addButtonHidden = true,
				createButtonHidden = true,
				createButtonInSelectionHidden = false,
				deleteButtonHidden = true,
				removeButtonHidden = true,
				exportButtonHidden = false,
				currentRow,
				hideItemFieldName,
				readOnly = false,
				disabled = false;

			Object.defineProperties(this, {
				// input attributes from directive
				addButtonHidden: {
					configurable: true,
					get: function () {
						return addButtonHidden;
					},
					set: function (newValue) {
						if (typeof newValue !== 'boolean') {
							return;
						}
						addButtonHidden = newValue;
					},
				},
				createButtonHidden: {
					configurable: true,
					get: function () {
						return createButtonHidden || this.editMode === GridEnums.EditMode.None;
					},
					set: function (newValue) {
						if (typeof newValue !== 'boolean') {
							return;
						}
						createButtonHidden = newValue;
					},
				},
				createButtonInSelectionHidden: {
					configurable: true,
					get: function () {
						return createButtonInSelectionHidden;
					},
					set: function (newValue) {
						if (typeof newValue !== 'boolean') {
							return;
						}
						createButtonInSelectionHidden = newValue;
					},
				},
				deleteButtonHidden: {
					configurable: true,
					get: function () {
						return deleteButtonHidden;
					},
					set: function (newValue) {
						if (typeof newValue !== 'boolean') {
							return;
						}
						deleteButtonHidden = newValue;
					},
				},
				removeButtonHidden: {
					configurable: true,
					get: function () {
						return removeButtonHidden || this.editMode === GridEnums.EditMode.None;
					},
					set: function (newValue) {
						if (typeof newValue !== 'boolean') {
							return;
						}
						removeButtonHidden = newValue;
					},
				},
				exportButtonHidden: {
					configurable: true,
					get: function () {
						return exportButtonHidden;
					},
					set: function (newValue) {
						if (typeof newValue !== 'boolean') {
							return;
						}
						exportButtonHidden = newValue;
					},
				},
				headerHidden: {
					configurable: true,
					get: function () {
						return headerHidden;
					},
					set: function (newValue) {
						if (typeof newValue !== 'boolean') {
							return;
						}
						headerHidden = newValue;
					},
				},
				showActionsPanel: {
					configurable: true,
					get: function () {
						return showActionsPanel;
					},
					set: function (newValue) {
						if (typeof newValue !== 'boolean') {
							return;
						}
						showActionsPanel = newValue;
					},
				},
				showSelectAll: {
					configurable: true,
					get: function () {
						return showSelectAll;
					},
					set: function (newValue) {
						if (typeof newValue !== 'boolean') {
							return;
						}
						showSelectAll = newValue;
					},
				},
				removeButtonDisabled: {
					configurable: true,
					writable: true,
					value: true,
				},
				deleteButtonDisabled: {
					configurable: true,
					writable: true,
					value: true,
				},
				editDisableFieldName: {
					writable: true,
				},
				removeDisableFieldName: {
					writable: true,
				},
				hideItemFieldName: {
					configurable: true,
					get: function () {
						return hideItemFieldName;
					},
					set: function (newValue) {
						hideItemFieldName = newValue;
						this.updateRequest();
					},
				},
				templateId: {
					configurable: true,
					writable: true,
				},
				wizardId: {
					configurable: true,
					writable: true,
				},
				outerContext: {
					configurable: true,
					writable: true,
				},
				addItemTitle: {
					configurable: true,
					writable: true,
				},
				editItemTitle: {
					configurable: true,
					writable: true,
				},
				enableDragAndDrop: {
					configurable: true,
					get: function () {
						return enableDragAndDrop;
					},
					set: function (newValue) {
						if (typeof newValue !== 'boolean') {
							return;
						}
						enableDragAndDrop = newValue;
						if (enableDragAndDrop) {
							this.enableSorting = false;
						}
					},
				},
				enableSorting: {
					configurable: true,
					get: function () {
						return enableSorting;
					},
					set: function (newValue) {
						if (typeof newValue !== 'boolean') {
							return;
						}
						enableSorting = newValue;
						if (enableDragAndDrop) {
							enableSorting = false;
						}
					},
				},
				enablePaging: {
					configurable: true,
					get: function () {
						return enablePaging;
					},
					set: function (newValue) {
						if (typeof newValue !== 'boolean') {
							return;
						}
						enablePaging = newValue;
					},
				},
				enableSearch: {
					configurable: true,
					get: function () {
						return enableSearch;
					},
					set: function (newValue) {
						if (typeof newValue !== 'boolean') {
							return;
						}
						enableSearch = newValue;
					},
				},
				enableColumnSearch: {
					configurable: true,
					get: function () {
						return enableColumnSearch;
					},
					set: function (newValue) {
						if (typeof newValue !== 'boolean') {
							return;
						}
						enableColumnSearch = newValue;
					},
				},
				enableAdvanceSearch: {
					configurable: true,
					get: function () {
						return enableAdvanceSearch;
					},
					set: function (newValue) {
						if (typeof newValue !== 'boolean') {
							return;
						}
						enableAdvanceSearch = newValue;
					},
				},
				inlineMode: {
					configurable: true,
					get: function () {
						return inlineMode || editMode === GridEnums.EditMode.Inline;
					},
					set: function (newValue) {
						inlineMode = Boolean(newValue);
						if (inlineMode) {
							editMode = GridEnums.EditMode.Inline;
						}
					},
				},
				editMode: {
					// none - 0, inline - 1, form - 2, wizard - 3, 4 - generic action
					configurable: true,
					get: function () {
						return editMode;
					},
					set: function (newValue) {
						if (inlineMode) {
							return;
						}
						editMode = GridEnums.EditMode.containsValue(newValue) ? newValue : GridEnums.EditMode.None;
					},
				},
				disableEdit: {
					configurable: true,
					get: function () {
						return disableEdit;
					},
					set: function (newValue) {
						if (typeof newValue !== 'boolean') {
							return;
						}
						disableEdit = newValue;
					},
				},
				// attributes from form
				parentView: {
					//parentView for back button in side panel
					configurable: true,
					writable: true,
				},
				serviceContext: {
					configurable: true,
					writable: true,
				},
				mxForm: {
					configurable: true,
					writable: true,
				},
				mxUiAccessibility: {
					configurable: true,
					writable: true,
				},
				currentRow: {
					configurable: true,
					get: function () {
						return currentRow;
					},
					set: function (newValue) {
						currentRow = newValue;
						this._currentRowHandlers.forEach(function (handler) {
							handler(currentRow);
						});
					},
				},
				_currentRowHandlers: {
					configurable: true,
					writable: false,
					value: [],
				},
				readOnly: {
					configurable: true,
					get: function () {
						return readOnly || this.mxForm?.readOnly || this.mxUiAccessibility?.readOnly;
					},
					set: function (newValue) {
						readOnly = Boolean(newValue);
					},
				},
				disabled: {
					configurable: true,
					get: function () {
						return disabled || this.mxForm?.disabled || this.mxUiAccessibility?.disabled;
					},
					set: function (newValue) {
						disabled = Boolean(newValue);
					},
				},
			});
		}

		async initDataQuery() {
			return $q.reject('initDataQuery is not implemented in base grid');
		}

		async initDataQueryExecutor(config) {
			const dataQuery = await this.initDataQuery();
			return gridHelperService.initExecutor(dataQuery, this.personalizationKey, config);
		}

		onChanges(changes) {
			if (changes.externalSelectedItems) {
				var selectedItems = changes.externalSelectedItems.currentValue;
				if (this.executor) {
					selectedItems = Array.isArray(selectedItems) ? selectedItems : [];
					this.executor.changeSelection(selectedItems);
				}
			}
		}

		onDestroy() {
			this.executor.onDestroy();
		}

		itemClickHandler(row) {
			this.currentRow = row;
			this.editRow(row).catch(mx.logCatch);
		}

		itemRightClickHandler(/*item*/) {}

		itemDblClickHandler(row) {
			this.currentRow = row;
			this.editRow(row).catch(mx.logCatch);
		}

		groupingClickHandler(/*column*/) {}

		reorderHandler() {}

		selectionHandler(data) {
			if (data.allSelectedRows.length === 0) {
				this.removeButtonDisabled = true;
				this.deleteButtonDisabled = true;
			} else {
				if (this.removeDisableFieldName) {
					this.removeButtonDisabled = data.allSelectedRows.some(function (row) {
						return row.getEntity()[this.removeDisableFieldName] === true;
					}, this);
				} else {
					this.removeButtonDisabled = false;
				}
				// deleteButtonDisabled is set from the real grid according to specific conditions
			}
		}

		async onAddButtonClick() {
			try {
				const entities = await this.findExistingEntities();
				this.afterEntitiesCreated(entities);
				this.updateRequest();
				this.highlightedRows = await this.executor.addEntitiesAsNewRows(entities);
			} catch (e) {
				mx.logCatch(e);
			}
		}

		async onCreateButtonClick(item) {
			try {
				await this.createNewItem(item._type, item._itemType);
			} catch (e) {
				mx.logCatch(e);
			}
		}

		isEntityEditable(entity) {
			if (!this.editDisableFieldName) {
				return true;
			}
			return entity[this.editDisableFieldName] !== true;
		}

		_modifyRow(row) {
			//on add or edit
			var entity = row.getEntity();
			var editable = this.isEntityEditable(entity);

			if (this.editMode === GridEnums.EditMode.None || this.editMode === GridEnums.EditMode.Inline || !editable) {
				return $q.when();
			}
			var notChangedEntity = angular.copy(entity);

			return this.editEntity(entity)
				.then(
					function (changedEntity) {
						this.executor.setEntity(row, changedEntity);
					}.bind(this)
				)
				.catch(function (e) {
					mx.SolutionBuilderAgent.ExtendDeep(entity, notChangedEntity);
					return $q.reject(e);
				});
		}

		editRow(row) {
			if (this.disableEdit) {
				return $q.when();
			} else {
				return this._modifyRow(row);
			}
		}

		editEntity(entity) {
			switch (this.editMode) {
				case GridEnums.EditMode.None:
				case GridEnums.EditMode.Inline:
					return $q.when(entity); // do nothing
				case GridEnums.EditMode.EditForm:
					return this.openEditForm(entity);
				case GridEnums.EditMode.Wizard:
					return this.openWizard(entity);
				case GridEnums.EditMode.GenericEdit:
					return this.runGenericEdit(entity);
				default:
					return $q.reject('not implemented for editMode = ' + this.editMode);
			}
		}

		async createNewItem(entityType, itemType) {
			let newEntity;
			try {
				newEntity = await this.executor.createEntity(entityType, itemType);
			} catch (e) {
				throw Error(e);
			}

			this.beforeEntityCreated(newEntity);
			const createdRow = await this.executor.createRow(newEntity);
			this.currentRow = createdRow;

			try {
				await this._modifyRow(createdRow);
			} catch (e) {
				this.afterEntityCreationCancelled(newEntity);
				this.currentRow = null;
				throw Error(e);
			}

			const isCorrect = await this.executor.isEntityCorrect(createdRow.getEntity());
			if (!isCorrect) {
				this.notifyCreatedItemIsIncorrect();
				throw Error(mx.noLogCatchMessage('created item is incorrect'));
			}

			this.afterEntitiesCreated([createdRow.getEntity()]);
			this.updateRequest();
			this.executor.addRows([createdRow]);
			this.highlightedRows = [createdRow];
			return createdRow;
		}

		notifyCreatedItemIsIncorrect() {
			notificationService.warning(i18n.get('solutionBuilderAgent.layouts.multiselection-tab.createdItemWasNotFound'));
		}

		async initCreateButtonItems() {
			return this.executor.allEntityTypes;
		}

		onRemoveButtonClick() {
			this.removeEntities(this.executor.allSelectedEntities).then(() => {
				this.executor.changeSelection([]);
			});
		}

		onDeleteButtonClick() {
			this.deleteItems(this.executor.allSelectedEntities).then(this.onRemoveButtonClick.bind(this));
		}

		findExistingEntities() {
			return $q.reject('not implemented');
		}

		removeEntities(/*allSelectedItems*/) {
			return $q.reject('not implemented');
		}

		beforeEntityCreated(/*newEntity*/) {}

		afterEntitiesCreated(/*newEntities*/) {}

		afterEntityCreationCancelled(/*newEntity*/) {}

		updateRequest() {}

		getPresetFields() {}

		runGenericPreview(entity) {
			return gridHelperService.runGenericPreviewAction({
				item: entity,
				parentView: this.parentView,
			});
		}

		async runGenericEdit(entity) {
			const changedEntity = await (this._isNewEntity(entity)
				? gridHelperService.runGenericCreateAction({
						_type: entity._type,
						_itemType: entity._itemType,
						parentView: this.parentView,
						presetFields: this.getPresetFields(),
				  })
				: gridHelperService.runGenericEditAction({
						item: entity,
						parentView: this.parentView,
						// presetFields: this.getPresetFields()
				  }));

			const isCorrect = await this.executor.isEntityCorrect(changedEntity);
			if (!isCorrect) {
				this.notifyCreatedItemIsIncorrect();
				throw Error(mx.noLogCatchMessage('created item is incorrect'));
			}

			return this.executor.loadEntityById(changedEntity._id);
		}

		_isNewEntity(entity) {
			return typeof entity._id === 'undefined';
		}

		_getEditTitle(isNew) {
			var title = isNew ? this.addItemTitle : this.editItemTitle;
			if (!title) {
				title = i18n.get(isNew ? 'solutionBuilderAgent.common.add_new' : 'solutionBuilderAgent.common.edit');
			}

			return title;
		}

		openEditForm(entity) {
			return gridHelperService.openEditForm(
				{
					item: entity,
					title: this._getEditTitle(this._isNewEntity(entity)),
					mxServiceContext: this.serviceContext,
					templateId: this.templateId,
					//TODO: is it possible to remove serviceContext here?
					dataModel: this.serviceContext?.mxServiceContext || this.mxForm?.entity || null,
					readOnly: this.readOnly,
					disabled: this.disabled,
				},
				this.parentView
			);
		}

		openWizard(entity) {
			return gridHelperService.openWizard({
				personalizationKey: this.personalizationKey,
				wizardId: this.wizardId,
				wizardHeader: this._getEditTitle(this._isNewEntity(entity)),
				entity: entity,
				outerContext: this.outerContext,
				parentView: this.parentView,
			});
		}

		addCurrentRowChangeHandler(handler) {
			if (typeof handler === 'function') {
				this._currentRowHandlers.push(handler);
			}
		}

		isGridReady() {
			return Boolean(this.initialized);
		}

		loadItems() {
			if (this.isGridReady()) {
				return this.executor.loadItems();
			} else {
				if (this.executor) {
					this.executor.resetItems();
				}
				return $q.when([]);
			}
		}
	}

	return BaseGrid;
}
